import { FC, ReactNode, useMemo } from 'react'
import {
  alpha,
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import {
  AccountBalanceOutlined,
  ArrowBack,
  ContentCopy,
  CreditCardOutlined,
  DownloadOutlined,
  ListAltOutlined,
  ReceiptOutlined
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useSnackbar } from 'notistack'

import { CONTRACT_DOCUMENT_TYPE, ContractDocumentTypes } from 'services/contracts/contracts.consts'
import { useActiveLeaseContractId } from 'services/contracts/hooks/useActiveLeaseContractId'
import { useLeaseContractsList } from 'services/contracts/hooks/useLeaseContracts'
import { useLeaseContractsManagementCompany } from 'services/contracts/hooks/useLeaseContractsManagementCompany'
import { useLeaseContractsRentBankAccount } from 'services/contracts/hooks/useLeaseContractsRentBankAccount'
import { useSchedulesWithDocumnets } from 'services/contracts/hooks/useSchedulesWithDocumnets'
import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import HelpdeskEmptyState from 'components/Helpdesk/HelpdeskEmptyState'
import theme from 'styles/theme'

import AdditionalServicesOnlinePaymentButton from './AdditionalServicesOnlinePaymentButton'
import {
  flattenScheduleGroups,
  formatMoney,
  getDocumentAmount,
  getDocumentPaidAmount
} from './leases.utils'

const CARD_SHADOW = '0px 1px 3px rgba(0, 0, 0, 0.06)'

const getDocumentTypeI18n = (type?: ContractDocumentTypes) => {
  switch (type) {
    case CONTRACT_DOCUMENT_TYPE.debitNote:
      return { id: 'paymentScheduleDocumnetTitle.debitNote', defaultMessage: 'Nota obciążeniowa' }
    case CONTRACT_DOCUMENT_TYPE.interestNote:
      return { id: 'paymentScheduleDocumnetTitle.interestNote', defaultMessage: 'Nota odsetkowa' }
    default:
      return { id: 'paymentScheduleDocumnetTitle.invoice', defaultMessage: 'Faktura VAT' }
  }
}

const removeBrTags = (str: string) => str.replace(/<br>/g, '')

const CopyField: FC<{ label: string; value?: string }> = ({ label, value }) => {
  const { enqueueSnackbar } = useSnackbar()
  const { formatMessage } = useIntl()

  const handleCopy = () => {
    if (!value) {
      return
    }

    const copy = 'clipboard' in navigator
      ? navigator.clipboard.writeText(value)
      : Promise.resolve(document.execCommand('copy', true, value))

    copy
      .then(() => {
        enqueueSnackbar(
          formatMessage({
            id: 'clipboardButton.success',
            defaultMessage: 'Dane poprawnie skopiowane do schowka.'
          }),
          { variant: 'success' }
        )
      })
      .catch(() => {
        enqueueSnackbar(
          formatMessage({
            id: 'clipboardButton.error',
            defaultMessage: 'Błąd. Nie można skopiować danych do schowka.'
          }),
          { variant: 'error' }
        )
      })
  }

  return (
    <Box>
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.3px',
          textTransform: 'uppercase',
          color: theme.r4r.grey,
          mb: '8px'
        }}>
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: '#f9fafb',
          border: `1px solid ${theme.r4r.greyLight}`,
          padding: '10px 12px'
        }}>
        <Typography
          sx={{
            flex: 1,
            fontSize: '12px',
            fontWeight: 600,
            color: theme.r4r.ink,
            wordBreak: 'break-all'
          }}>
          {value || '—'}
        </Typography>
        <IconButton
          onClick={handleCopy}
          disabled={!value}
          size='small'
          sx={{ color: theme.r4r.navy, borderRadius: 0 }}>
          <ContentCopy sx={{ fontSize: '16px' }} />
        </IconButton>
      </Box>
    </Box>
  )
}

const SectionTitle: FC<{ icon: ReactNode; children: ReactNode }> = ({
  icon,
  children
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '16px' }}>
    <Box sx={{ color: theme.r4r.navy, display: 'flex' }}>{icon}</Box>
    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: theme.r4r.navy }}>
      {children}
    </Typography>
  </Box>
)

const cardSx = {
  backgroundColor: '#fff',
  boxShadow: CARD_SHADOW,
  padding: '24px 20px'
} as const

const detailRowSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
  py: '10px',
  borderBottom: `1px solid ${theme.r4r.greyLight}`
} as const

// Invoice preview ("Panel Klienta 3.0": Figma "Umowy — Podgląd faktury").
const AdditionalServicesInvoicePreview: FC = () => {
  const { formatMessage } = useIntl()
  const muiTheme = useTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))
  const navigate = useNavigate()
  const { id, documentType, documentId } = useParams()
  const { activeLeaseContractId } = useActiveLeaseContractId()

  const unpaid = useSchedulesWithDocumnets({
    activeLeaseContractId,
    isPaid: false
  })
  const paid = useSchedulesWithDocumnets({
    activeLeaseContractId,
    isPaid: true
  })

  const { getActiveContract } = useLeaseContractsList()
  const activeContract = useMemo(
    () => getActiveContract(activeLeaseContractId),
    [activeLeaseContractId, getActiveContract]
  )

  const { leaseContractsManagementCompany } =
    useLeaseContractsManagementCompany(activeLeaseContractId)
  const { leaseContractsRentBankAccount } = useLeaseContractsRentBankAccount(activeLeaseContractId)

  const document = useMemo(() => {
    const all = [
      ...flattenScheduleGroups(unpaid.schedulesWithDocumnets),
      ...flattenScheduleGroups(paid.schedulesWithDocumnets)
    ]

    return all.find(
      (item) => String(item.id) === documentId && item.type === documentType
    )
  }, [documentId, documentType, paid.schedulesWithDocumnets, unpaid.schedulesWithDocumnets])

  const isLoading = unpaid.isLoading || paid.isLoading
  const currency = activeContract?.paymentCurrency ?? ''
  const amount = document ? getDocumentAmount(document) : '0'
  const paidAmount = document ? getDocumentPaidAmount(document) : '0'
  const remaining = Math.max(0, parseFloat(amount || '0') - parseFloat(paidAmount || '0'))
  const isPaid = !!document?.isPaid
  const items = (document?.items || document?.positions || []) as {
    id: number
    title: string
    amountGross?: string
    amount?: string
  }[]
  const canPay =
    !!document &&
    !isPaid &&
    currency === 'PLN' &&
    !activeContract?.isOperationsFrozen &&
    document.type !== CONTRACT_DOCUMENT_TYPE.paymentSchedule

  const relatedInvoices = useMemo(() => {
    const related: { id: number; number: string; file?: string | null }[] = []
    let current: any = document

    while (current?.parent) {
      related.push({
        id: current.parent.id,
        number: current.parent.number,
        file: current.parent.file
      })
      current = current.parent
    }

    return related
  }, [document])

  const statusBadge = isPaid
    ? {
        label: formatMessage({ id: 'paymentSchedule.statusPaid', defaultMessage: 'Opłacona' }),
        color: '#fff',
        backgroundColor: alpha('#fff', 0.2)
      }
    : {
        label: formatMessage({ id: 'paymentSchedule.statusToPay', defaultMessage: 'Do zapłaty' }),
        color: '#fff',
        backgroundColor: alpha(theme.r4r.yellow, 0.3)
      }

  const backToSchedule = () => navigate(`/additional-services/${id}/payment-schedule`)

  const header = isDesktop ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '20px' }}>
      <IconButton
        onClick={backToSchedule}
        sx={{ width: '36px', height: '36px', color: theme.r4r.navy, borderRadius: 0 }}>
        <ArrowBack sx={{ fontSize: '22px' }} />
      </IconButton>
      <Typography
        sx={{ fontSize: '24px', fontWeight: 700, lineHeight: '28.8px', color: theme.r4r.navy }}>
        {formatMessage({
          id: 'additionalServicesInvoicePreview.title',
          defaultMessage: 'Podgląd faktury'
        })}
      </Typography>
    </Box>
  ) : null

  const content = (() => {
    if (isLoading) {
      return null
    }

    if (!document) {
      return (
        <>
          {header}
          <HelpdeskEmptyState
            Icon={ReceiptOutlined}
            title={formatMessage({
              id: 'additionalServicesPaymentSchedule.noDocuments',
              defaultMessage: 'Brak dokumentów.'
            })}
          />
        </>
      )
    }

    return (
      <>
        {header}
        <Box
          sx={{
            background: theme.r4r.gradient,
            boxShadow: theme.r4r.cardShadow,
            padding: isDesktop ? '28px 32px' : '20px',
            mb: isDesktop ? '32px' : '16px',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { md: 'center' },
            justifyContent: 'space-between',
            gap: '16px'
          }}>
          <Box>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: alpha('#fff', 0.6)
              }}>
              {document.number}
            </Typography>
            <Typography sx={{ fontSize: '20px', fontWeight: 800, color: '#fff', mt: '4px' }}>
              {formatMessage(getDocumentTypeI18n(document.type))}
            </Typography>
          </Box>
          <Box sx={{ textAlign: { md: 'right' } }}>
            <Typography sx={{ fontSize: '36px', fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>
              {formatMoney(amount, currency)}
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                backgroundColor: statusBadge.backgroundColor,
                padding: '4px 10px',
                mt: '8px'
              }}>
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: statusBadge.color
                }}>
                {statusBadge.label}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: isDesktop ? '32px' : '16px',
            mb: '24px'
          }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: isDesktop ? '20px' : '16px' }}>
            <Box sx={cardSx}>
              <SectionTitle icon={<ListAltOutlined sx={{ fontSize: '20px' }} />}>
                {formatMessage({
                  id: 'paymentSchedule.invoiceItems',
                  defaultMessage: 'Pozycje faktury'
                })}
              </SectionTitle>
              {items.map((item) => (
                <Box key={`${document.number}_${item.id}`} sx={detailRowSx}>
                  <Typography sx={{ fontSize: '14px', color: theme.r4r.ink }}>
                    {removeBrTags(item.title)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: theme.r4r.ink,
                      whiteSpace: 'nowrap'
                    }}>
                    {formatMoney(item.amountGross || item.amount, currency)}
                  </Typography>
                </Box>
              ))}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  pt: '14px',
                  mt: items.length ? '4px' : 0,
                  borderTop: `2px solid ${theme.r4r.navy}`
                }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: theme.r4r.navy }}>
                  {formatMessage({
                    id: 'paymentScheduleDocumnet.sum',
                    defaultMessage: 'Razem'
                  })}
                </Typography>
                <Typography sx={{ fontSize: '18px', fontWeight: 800, color: theme.r4r.navy }}>
                  {formatMoney(amount, currency)}
                </Typography>
              </Box>
              {relatedInvoices.length > 0 && (
                <Box sx={{ mt: '16px' }}>
                  <Typography
                    sx={{ fontSize: '12px', fontWeight: 700, color: theme.r4r.navy, mb: '4px' }}>
                    {formatMessage({
                      id: 'additionalServicesPaymentSchedule.relatedInvoices',
                      defaultMessage: 'faktury powiązane:'
                    })}
                  </Typography>
                  {relatedInvoices.map((invoice) =>
                    invoice.file ? (
                      <Button
                        key={invoice.id}
                        href={invoice.file}
                        target='_blank'
                        sx={{
                          display: 'block',
                          textDecoration: 'none',
                          fontSize: '13px',
                          fontWeight: 700,
                          color: theme.r4r.navy,
                          justifyContent: 'flex-start',
                          px: 0
                        }}>
                        {invoice.number}
                      </Button>
                    ) : (
                      <Typography
                        key={invoice.id}
                        sx={{ fontSize: '13px', fontWeight: 700, color: theme.r4r.navy }}>
                        {invoice.number}
                      </Typography>
                    )
                  )}
                </Box>
              )}
            </Box>

            <Box sx={cardSx}>
              <SectionTitle icon={<AccountBalanceOutlined sx={{ fontSize: '20px' }} />}>
                {formatMessage({
                  id: 'additionalServicesInvoicePreview.paymentStatus',
                  defaultMessage: 'Status płatności'
                })}
              </SectionTitle>
              <Box sx={detailRowSx}>
                <Typography sx={{ fontSize: '13px', color: theme.r4r.grey }}>
                  {formatMessage({
                    id: 'paymentScheduleDocumnet.receivable',
                    defaultMessage: 'Należność'
                  })}
                </Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: theme.r4r.ink }}>
                  {formatMoney(amount, currency)}
                </Typography>
              </Box>
              <Box sx={detailRowSx}>
                <Typography sx={{ fontSize: '13px', color: theme.r4r.grey }}>
                  {formatMessage({
                    id: 'paymentScheduleDocumnet.paid',
                    defaultMessage: 'Opłacone'
                  })}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 700,
                    color: parseFloat(paidAmount || '0') === 0 ? theme.r4r.red : theme.r4r.ink
                  }}>
                  {formatMoney(paidAmount, currency)}
                </Typography>
              </Box>
              <Box sx={{ ...detailRowSx, borderBottom: 'none', pb: 0 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 800, color: theme.r4r.navy }}>
                  {formatMessage({
                    id: 'paymentScheduleDocumnet.remaining',
                    defaultMessage: 'Pozostało do zapłaty'
                  })}
                </Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 800, color: theme.r4r.navy }}>
                  {formatMoney(remaining, currency)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {!isPaid && !activeContract?.isOperationsFrozen && (
            <Box sx={cardSx}>
              <SectionTitle icon={<CreditCardOutlined sx={{ fontSize: '20px' }} />}>
                {formatMessage({
                  id: 'additionalServicesPaymentDetailsButton.bankTransferDetails',
                  defaultMessage: 'Dane do przelewu'
                })}
              </SectionTitle>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <CopyField
                  label={formatMessage({
                    id: 'additionalServicesInvoicePreview.recipient',
                    defaultMessage: 'Odbiorca'
                  })}
                  value={leaseContractsManagementCompany?.name}
                />
                <CopyField
                  label={formatMessage({
                    id: 'additionalServicesInvoicePreview.accountNumber',
                    defaultMessage: 'Numer konta'
                  })}
                  value={leaseContractsRentBankAccount?.paymentBankAccount}
                />
                <CopyField
                  label={formatMessage({
                    id: 'additionalServicesInvoicePreview.transferTitle',
                    defaultMessage: 'Tytuł przelewu'
                  })}
                  value={document.number}
                />
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: '12px'
          }}>
          {canPay && (
            <AdditionalServicesOnlinePaymentButton
              documentType={document.type}
              documentId={document.id}
              variant='contained'
              fullWidth
              label={formatMessage({
                id: 'additionalServicesOnlinePaymentButton.payNow',
                defaultMessage: 'Opłać teraz'
              })}
              sx={{
                backgroundColor: theme.r4r.yellow,
                color: theme.r4r.navy,
                fontWeight: 700,
                fontSize: '14px',
                py: '12px',
                boxShadow: 'none',
                borderRadius: 0,
                ':hover': { backgroundColor: theme.r4r.yellow }
              }}
            />
          )}
          {!!document.file && (
            <Button
              href={document.file}
              target='_blank'
              startIcon={<DownloadOutlined />}
              sx={{
                textDecoration: 'none',
                border: `1px solid ${theme.r4r.navy}`,
                color: theme.r4r.navy,
                fontWeight: 700,
                fontSize: '14px',
                py: '11px',
                px: '24px',
                borderRadius: 0,
                whiteSpace: 'nowrap',
                minWidth: isDesktop ? '200px' : undefined
              }}>
              {formatMessage({
                id: 'additionalServicesInvoicePreview.downloadPdf',
                defaultMessage: 'Pobierz PDF'
              })}
            </Button>
          )}
        </Box>
      </>
    )
  })()

  if (isDesktop) {
    return content
  }

  return (
    <>
      <BackNavigationHeader
        label={formatMessage({
          id: 'additionalServicesInvoicePreview.title',
          defaultMessage: 'Podgląd faktury'
        })}
        navigateTo={`/additional-services/${id}/payment-schedule`}
      />
      <BackNavigationContent>
        <Box pt='env(safe-area-inset-top)'>{content}</Box>
      </BackNavigationContent>
    </>
  )
}

export default AdditionalServicesInvoicePreview
